import React from 'react';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';

export const PartnerPage: React.FC = () => {
  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-8 pb-12 max-w-3xl mx-auto">
        {/* Page Header */}
        <div className="flex w-full flex-col items-start gap-2">
          <span className="text-heading-1 font-heading-1 text-default-font">
            XRP.Deals Partner Program
          </span>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* The Model Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            The Model
          </span>
          <p className="text-body font-body text-subtext-color">
            We're setting aside 25% of all XRP.Deals broker fee revenue to share with projects that partner with us as their official marketplace. That 25% is split into two pieces:
          </p>

          <div className="w-full rounded-lg border border-brand-200 bg-brand-50 p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Pioneer Pool (10%)
                </span>
                <p className="text-body font-body text-subtext-color">
                  The first 10 projects to join each get a guaranteed 1% of all platform revenue, in perpetuity. This is the reward for being early and taking the leap before Deals has fully proven itself.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Performance Pool (15%)
                </span>
                <p className="text-body font-body text-subtext-color">
                  Shared among all partner projects proportionally based on the broker fees generated by each project's activity. More volume from your collections = bigger slice of this pool.
                </p>
              </div>
            </div>
          </div>

          <p className="text-body font-body text-subtext-color">
            These stack — so if you're one of the first 10, you get your guaranteed 1% AND you participate in the Performance Pool based on your project's activity. Payouts are quarterly.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* What Partnering Means Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            What Partnering Means
          </span>
          <ul className="list-disc list-inside text-body font-body text-subtext-color space-y-2 pl-2">
            <li>When you share links to your collections on X, Discord, Telegram, etc., you link to them on XRP.Deals rather than other marketplaces</li>
            <li>New collection mints launch on Deals</li>
            <li>If you have a sales or listing bot in your Discord, switch to ours (since it links back to your Hub on Deals)</li>
          </ul>
          <p className="text-body font-body text-subtext-color">
            Basically, when your community asks "where do I buy or sell," the answer is XRP.Deals.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* What You Get Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            What You Get Beyond the Revenue Share
          </span>
          <p className="text-body font-body text-subtext-color">
            Your project gets a Hub on Deals — a single home for your collections, token info, and project updates all in one place. Your community doesn't have to bounce between different platforms to see everything. That's something no other XRPL marketplace is doing right now.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Why We're Doing This Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Why We're Doing This
          </span>
          <p className="text-body font-body text-subtext-color">
            We need projects to help bootstrap this. You'd be taking a real bet on Deals before there's a ton of liquidity or user traffic, and that risk deserves compensation beyond just "we'll treat you well." The guaranteed 1% is our commitment that early partners share in the upside as the platform grows — not just from your own project's activity, but from everything.
          </p>
          <p className="text-body font-body text-subtext-color">
            And the Performance Pool means that as your project drives more volume, you're always earning more. There's no cap.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* The Bottom Line Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            The Bottom Line
          </span>
          <p className="text-body font-body text-subtext-color">
            If Deals captures even a fraction of XRPL NFT activity, 1%+ of platform revenue becomes a meaningful number. And because it's in perpetuity, you're not just getting a one-time incentive — you're getting an ownership-like stake in the platform's success.
          </p>
        </div>

        <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />

        {/* Conditions Section */}
        <div className="flex w-full flex-col items-start gap-4">
          <span className="text-heading-2 font-heading-2 text-default-font">
            Conditions on the Pioneer Pool
          </span>
          <p className="text-body font-body text-subtext-color">
            The guaranteed 1% is yours as long as you're actually partnered with us. The following would forfeit your slot:
          </p>

          <div className="w-full rounded-lg border border-neutral-border bg-default-background p-6">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Launching elsewhere
                </span>
                <p className="text-body font-body text-subtext-color">
                  If you mint a new collection on another marketplace, you forfeit the Pioneer slot.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Dormancy
                </span>
                <p className="text-body font-body text-subtext-color">
                  If a full year passes with no secondary sales brokered by XRP.Deals across any of your collections, the project is considered inactive and the slot is forfeited.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Ownership Transfer
                </span>
                <p className="text-body font-body text-subtext-color">
                  If project ownership changes hands, the new owner must be approved by XRP.Deals to retain the Pioneer slot and 1% revenue share. Transfers without approval result in forfeiture.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  One Slot Per Team/Person
                </span>
                <p className="text-body font-body text-subtext-color">
                  Pioneer slots are limited to one per team and one per person. If you manage multiple projects, only one can hold a Pioneer slot — you cannot receive the guaranteed 1% separately for each project.
                </p>
              </div>
              <div className="flex flex-col gap-2">
                <span className="text-body-bold font-body-bold text-default-font">
                  Conduct
                </span>
                <p className="text-body font-body text-subtext-color">
                  Actions that actively harm the platform — such as publicly disparaging XRP.Deals, spreading false information, or engaging in behavior that damages the platform's reputation (e.g., scams, rug pulls) — are grounds for forfeiture of the Pioneer revenue share.
                </p>
              </div>
            </div>
          </div>

          <p className="text-body font-body text-subtext-color">
            In any case, you'd still be welcome in the Performance Pool — you'd just lose the guaranteed piece.
          </p>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
